import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPost } from '../services/apiService';
import ChatInterface, { ChatInterfaceRef } from '../components/ChatInterface';
import CaseLimitModal from '../components/CaseLimitModal';
import UpgradeScreen from '../components/UpgradeScreen';
import SEO from '../components/SEO';

export default function AppPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [subStatus, setSubStatus] = useState<'loading' | 'validating' | 'active' | 'inactive'>('loading');
  const [planKey, setPlanKey] = useState<string>('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (sessionId) {
      window.history.replaceState({}, '', window.location.pathname);
      setSubStatus('validating');

      let attempts = 0;
      const maxAttempts = 15;
      const interval = setInterval(async () => {
        attempts++;
        try {
          const { status, planKey: pk } = await apiGet<{ status: string; planKey: string | null }>('/subscription/status');
          if (status === 'active' || status === 'past_due') {
            clearInterval(interval);
            if (pk) setPlanKey(pk);
            setSubStatus('active');
          } else if (attempts >= maxAttempts) {
            clearInterval(interval);
            setSubStatus('inactive');
          }
        } catch {
          if (attempts >= maxAttempts) {
            clearInterval(interval);
            setSubStatus('inactive');
          }
        }
      }, 2000);

      return () => clearInterval(interval);
    }

    apiGet<{ status: string; planKey: string | null }>('/subscription/status')
      .then(({ status, planKey: pk }) => {
        setSubStatus(status === 'active' || status === 'past_due' ? 'active' : 'inactive');
        if (pk) setPlanKey(pk);
      })
      .catch(() => setSubStatus('inactive'));
  }, []);

  // Redirect to subscribe page when inactive
  useEffect(() => {
    if (subStatus === 'inactive') {
      navigate('/subscribe', { replace: true });
    }
  }, [subStatus, navigate]);

  if (subStatus === 'loading' || subStatus === 'validating') {
    return (
      <div className="App">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '1rem', color: '#64748b' }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="spin-icon" aria-hidden="true" role="presentation">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <p style={{ margin: 0, fontSize: '0.95rem' }}>
            {subStatus === 'validating' ? 'Validando sua assinatura...' : 'Carregando...'}
          </p>
          {subStatus === 'validating' && (
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>Isso pode levar alguns segundos.</p>
          )}
        </div>
      </div>
    );
  }

  if (subStatus === 'inactive') {
    return null; // Will redirect via useEffect
  }

  return (
    <AppInner
      onLogout={logout}
      currentPlanKey={planKey}
      onInvalidSubscription={() => navigate('/subscribe', { replace: true })}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AppInner — shown when the user has an active subscription
// ─────────────────────────────────────────────────────────────────────────────
function AppInner({ onLogout, currentPlanKey, onInvalidSubscription }: { onLogout: () => void; currentPlanKey: string; onInvalidSubscription: () => void }) {
  const chatInterfaceRef = useRef<ChatInterfaceRef>(null);
  const handleNewSession = useCallback(() => { chatInterfaceRef.current?.handleNewSession(); }, []);
  const [caseLimitPeriodEnd, setCaseLimitPeriodEnd] = useState<number | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const handlePortal = async () => {
    if (!currentPlanKey || currentPlanKey === 'free') {
      setShowUpgrade(true);
      return;
    }
    setPortalLoading(true);
    try {
      const { url } = await apiPost<{ url: string }>('/checkout/portal', {});
      window.location.href = url;
    } catch (err) {
      console.error('Portal error:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpgrade = () => {
    setCaseLimitPeriodEnd(null);
    setShowUpgrade(true);
  };

  return (
    <div className="flex flex-col h-screen h-[100dvh] w-full max-w-[100vw] bg-background overflow-y-auto overflow-x-hidden">
      <SEO
        title="Simulação"
        description="Pratique diagnóstico médico com pacientes virtuais gerados por IA."
        path="/app"
        noindex
      />
      <header className="sticky top-0 z-50 w-full bg-[#171f33]/80 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 md:px-8 py-3 max-w-7xl mx-auto">
          <div className="text-2xl font-bold tracking-tighter text-sky-400 font-headline">
            MedPuzzle
          </div>
          <div className="flex items-center gap-2">
            <button
              className="flex items-center gap-2 px-4 py-2 bg-surface-container-high border-none rounded-lg text-on-surface-variant text-sm font-label font-medium hover:bg-surface-container-highest hover:text-on-surface active:scale-95 transition-all"
              aria-label="Nova Sessão"
              onClick={handleNewSession}
            >
              <span className="material-symbols-outlined text-base">add_circle</span>
              <span className="hidden sm:inline">Nova Sessão</span>
            </button>
            <button
              className="flex items-center justify-center w-9 h-9 bg-surface-container-high border-none rounded-lg text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface active:scale-95 transition-all"
              onClick={handlePortal}
              disabled={portalLoading}
              aria-label="Minha conta"
            >
              <span className="material-symbols-outlined text-lg">account_circle</span>
            </button>
            <button
              className="flex items-center justify-center w-9 h-9 bg-surface-container-high border-none rounded-lg text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface active:scale-95 transition-all"
              onClick={onLogout}
              aria-label="Sair da conta"
            >
              <span className="material-symbols-outlined text-lg">logout</span>
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-[1400px] mx-auto w-full px-2 md:px-4 flex flex-col min-h-0">
        <ChatInterface ref={chatInterfaceRef} onUpgrade={handleUpgrade} onCaseLimit={setCaseLimitPeriodEnd} onInvalidSubscription={onInvalidSubscription} planKey={currentPlanKey} />
      </main>
      {caseLimitPeriodEnd !== null && (
        <CaseLimitModal
          periodEnd={caseLimitPeriodEnd}
          onUpgrade={handleUpgrade}
          onClose={() => setCaseLimitPeriodEnd(null)}
        />
      )}
      {showUpgrade && (
        <UpgradeScreen
          currentPlanKey={currentPlanKey}
          onBack={() => setShowUpgrade(false)}
          onUpgradeComplete={() => setShowUpgrade(false)}
        />
      )}
    </div>
  );
}
